import { useEffect, useRef, useState } from "react";

interface SpinningIcoProps {
  /** Canvas pixel size (width and height). */
  size?: number;
  /** Hex colour for the wireframe. Defaults to Whistll volt. */
  color?: string;
  className?: string;
}

/**
 * Dual concentric wireframe icosahedrons slowly counter-rotating. Ported from
 * apps/mobile/src/components/shared/IcosahedronBadge.jsx so the web marketing
 * site uses the same brand geometry as the app.
 *
 * Three.js is dynamically imported so it lands in its own chunk and the rest
 * of the marketing bundle stays small. Renders to a transparent canvas so it
 * composites cleanly onto any background.
 *
 * Falls back to a static SVG ring if WebGL can't be created (old hardware,
 * GPU blocklisted, privacy extensions) so the brand mark still shows up
 * instead of empty space.
 */
export function SpinningIco({
  size = 220,
  color = "#FF6200",
  className,
}: SpinningIcoProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let animId: number | undefined;
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    void import("three")
      .then((THREE) => {
        if (cancelled || !canvasRef.current) return;

        let renderer: InstanceType<typeof THREE.WebGLRenderer>;
        try {
          renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha: true,
          });
        } catch (err) {
          // WebGL unavailable - fall back to the static SVG ring so users
          // on older hardware / blocked GPUs / privacy extensions still
          // see the brand mark.
          if (!cancelled) setFallback(true);
          return;
        }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(size, size, false);
      renderer.setClearColor(0x000000, 0);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
      camera.position.set(0, 0, 2.8);

      const voltColor = new THREE.Color(color);

      const outerGeo = new THREE.IcosahedronGeometry(1.0, 1);
      const outerEdges = new THREE.EdgesGeometry(outerGeo);
      const outerMat = new THREE.LineBasicMaterial({
        color: voltColor,
        transparent: true,
        opacity: 0.45,
      });
      const outer = new THREE.LineSegments(outerEdges, outerMat);
      scene.add(outer);

      const innerGeo = new THREE.IcosahedronGeometry(0.6, 1);
      const innerEdges = new THREE.EdgesGeometry(innerGeo);
      const innerMat = new THREE.LineBasicMaterial({
        color: voltColor,
        transparent: true,
        opacity: 0.2,
      });
      const inner = new THREE.LineSegments(innerEdges, innerMat);
      scene.add(inner);

      const start = performance.now();
      const animate = () => {
        animId = requestAnimationFrame(animate);
        const t = (performance.now() - start) / 1000;
        outer.rotation.x = t * 0.12;
        outer.rotation.y = t * 0.18;
        inner.rotation.x = -t * 0.09;
        inner.rotation.y = t * 0.14;
        renderer.render(scene, camera);
      };
      animate();

      cleanup = () => {
        if (animId !== undefined) cancelAnimationFrame(animId);
        outerGeo.dispose();
        outerEdges.dispose();
        outerMat.dispose();
        innerGeo.dispose();
        innerEdges.dispose();
        innerMat.dispose();
        renderer.dispose();
      };
      })
      .catch(() => {
        // Dynamic import failed (network flake / stale chunk URL after
        // a deploy). Fall back so the page still renders.
        if (!cancelled) setFallback(true);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [size, color]);

  if (fallback) {
    // Static SVG ring, proportions match the WebGL geometry roughly.
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden="true"
      >
        <circle
          cx="16"
          cy="16"
          r="10.56"
          stroke={color}
          strokeWidth="3.52"
          fill="none"
        />
        <line
          x1="16"
          y1="2.72"
          x2="16"
          y2="29.28"
          stroke={color}
          strokeWidth="3.52"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size }}
      className={className}
      aria-hidden="true"
    />
  );
}
